/**
 * /ai-prd — product landing page targeting the "ai prd" / "ai prd generator" /
 * "ai prd tool" / "write prd with ai" cluster.
 * Differentiation angle: a wave of tools now generate a PRD with AI in one click,
 * but generation is only day one. The real pain starts after the doc exists —
 * the product moves, decisions change, and the PRD silently drifts out of date.
 * Other AI PRD tools help you write it; Draftlize keeps it from rotting after you
 * do — every decision becomes an addressable card, change one and dependents
 * auto-flag stale, and the agent always reads the current version.
 *
 * English-only + inline (no i18n), mirroring the comparison / guide pages.
 * Chinese variant lives in AiPrdZh.tsx.
 */
import { MarketingShell } from './MarketingShell';
import { TemplateCta } from './TemplateCta';

export function AiPrd() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/ai-prd"
      documentTitle="AI PRD Tool: Draft It, Then Keep It From Drifting · Draftlize"
      documentDescription="Plenty of tools generate a PRD with AI in one click. But generation is only the first day — once the PRD exists, the product moves and the doc silently drifts. Draftlize makes every decision in your PRD a card: change one, everything that depends on it auto-flags stale, and your AI agent always reads the current version."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Product · AI PRD</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            AI can write the PRD.<br/>
            <em>Keeping it true is the hard part.</em>
          </h1>
          <p className="lede">
            Generating a <a href="/product-requirements-document" style={{ color: 'var(--amber)' }}>PRD</a> with AI is a solved problem — a dozen tools will draft one from a prompt in seconds. But a PRD isn't done the day it's written; that's the day it starts to drift. The product moves, a decision changes, and the doc that read perfectly on Monday is quietly wrong by Friday — and nobody knows which paragraph went stale. Draftlize handles the part the generators skip: it turns every decision in your PRD into an addressable card, so when one changes, everything that depended on it flags itself stale — and the agent always reads the version that's still true.
          </p>
          <div style={{ marginTop: 32, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <a className="btn-primary" href="/">Start free with $5</a>
            <span style={{ fontSize: 14, color: 'var(--ink-2)' }}>No card. Pay only for what you use.</span>
          </div>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">Generating a PRD vs <em>keeping one alive.</em></h2>
          </div>
          <p>An AI PRD generator hands you a polished document and walks away. From that moment it's a snapshot — every decision change makes it a little more wrong, and nothing tells you which part. Draftlize is built for the long tail after the first draft, where the actual cost lives.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>A typical AI PRD generator</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>First draft from a prompt</td><td className="ours yes">Yes — drafted into structured cards</td><td className="partial">Yes — drafted as one prose doc</td></tr>
            <tr><td>What you get back</td><td className="ours yes">Decisions, specs, dependencies as cards</td><td className="no">A wall of Markdown</td></tr>
            <tr><td>A decision changes next week</td><td className="ours yes">Dependents auto-flag stale</td><td className="no">Regenerate from scratch, lose your edits</td></tr>
            <tr><td>Which paragraph is now wrong?</td><td className="ours yes">The stale cards tell you</td><td className="no">You re-read the whole thing</td></tr>
            <tr><td>Can an AI agent read it back?</td><td className="ours yes">Native read + write via MCP</td><td className="partial">Only the text you paste in</td></tr>
            <tr><td>Six PRDs sharing one decision</td><td className="ours yes">One card, cited from all six</td><td className="no">Six copies that drift apart</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why the generated PRD <em>drifts.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Generation is day one of a long life</h3>
            <p>The PRD you generate on Monday is the easy 10%. The hard 90% is the weeks after — when scope shifts, a constraint loosens, a metric gets renamed. The generator was never there for that part; it shipped you a document and moved on.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">A prose doc can't know what changed</h3>
            <p>When you change the pricing decision, the three requirements that assumed the old price are now wrong. But a Markdown file has no idea those sentences are connected — so they sit there reading as truth until someone trips over them in review.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Re-generating throws away your thinking</h3>
            <p>When the PRD drifts far enough, the generator's only answer is "generate it again" — which discards every edit, comment, and hard-won decision you made by hand. You're not maintaining a document; you're reprinting it and hoping.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">From <em>generating</em> a PRD to <em>maintaining</em> one.</h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Draft into cards, not into a wall of text</h3>
            <p>Draftlize still drafts your PRD with AI — but it lands as structured cards: each decision, requirement, and open question is its own addressable unit, with its rationale and dependencies attached. The same first draft, in a shape that can be maintained instead of just re-printed.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Change one decision, dependents flag stale</h3>
            <p>Every card declares what it depends on. Change the pricing decision and every requirement built on it turns stale automatically — the way a build system invalidates everything downstream of a changed file. The drift stops being silent.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">The agent always reads the current version</h3>
            <p>Claude Code or Cursor reads your PRD through MCP — and reads the live cards, not a stale paste from last week. It writes back too, so the next decision lands as a card the rest of the PRD can depend on. The document and the agent stay in sync by construction.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Other AI PRD tools help you write it. Draftlize keeps it from rotting after you do.
        </blockquote>
        <cite>Generating the first draft is the easy part — staying true to it is the work.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What is an AI PRD?', a: 'An AI PRD is a product requirements document drafted with an AI model from a short prompt or a few notes, instead of written by hand from a blank page. Most AI PRD tools generate the document in seconds. The harder part is the weeks after: once the product moves and a decision changes, the generated document drifts out of date and nothing tells you which part.' },
            { q: 'What is the best AI PRD generator?', a: 'The right tool depends on what you need after the first draft. If you only need a one-time draft, most AI writing tools produce one. If you need the PRD to stay correct as decisions change, you want the requirements stored as linked units rather than one prose blob — Draftlize drafts into addressable cards so that when a decision changes, every dependent requirement flags itself stale.' },
            { q: 'Can ChatGPT write a PRD?', a: 'Yes — ChatGPT or Claude will draft a reasonable PRD from a good prompt. The limitation is not the writing, it is the memory: a chat has no persistent, addressable state, so the next session does not know what the last one decided, and the document it produced starts drifting the moment you paste it into a doc.' },
            { q: 'How is Draftlize different from an AI PRD generator?', a: 'A generator hands you a finished document and walks away. Draftlize is built for the long tail after the first draft: every decision in the PRD becomes a card an AI agent reads and writes over MCP, so when one changes, everything that depended on it auto-flags stale and the agent always reads the version that is still true.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Write your PRD with AI — <em>then keep it true.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Draft a PRD from a prompt, let it land as cards, and let the agent keep every decision — and everything that depends on it — current as the product moves.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
      <TemplateCta template="prd" />
    </MarketingShell>
  );
}
